import {ArrowRight, Plus, Rocket, ShieldCheck, TrendingUp} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Layout from '@/components/Layout'
import ProjectCard from '@/components/ProjectCard'
import RecentlyBought from '@/components/RecentlyBought'
import SeoHead from '@/components/SeoHead'
import SimLaunchCard from '@/components/SimLaunchCard'
import { useSimLaunchList } from '@/hooks/useSimLaunchpad'
import { useSimPresales } from '@/hooks/useSimPresales'
import { isLaunchGraduated } from '@/lib/launchpad/curveMath'
import { listLivePresaleProjects } from '@/lib/presale/listings'
import { prefetchProjectLinks } from '@/lib/projectLinks'

type HomeListingTab = 'launches' | 'presales'

function Home() {
  const { tick } = useSimPresales()
  const { launches } = useSimLaunchList()
  const [listingTab, setListingTab] = useState<HomeListingTab>('launches')
  const featuredPresale = useMemo(() => listLivePresaleProjects().slice(0, 6), [tick])
  const featuredCurves = useMemo(
    () => launches.filter(launch => !isLaunchGraduated(launch)).slice(0, 6),
    [launches],
  )

  useEffect(() => {
    prefetchProjectLinks([
      ...featuredCurves.flatMap(launch => [launch.contractAddress, launch.shareSlug, launch.id, launch.tokenAddress]),
      ...featuredPresale.flatMap(project => [project.contractAddress, project.shareSlug, project.id, project.tokenAddress]),
    ])
  }, [featuredCurves, featuredPresale])

  return (
    <Layout>
      <SeoHead
        title="Fair-Launch Bonding Curves & Community Presales"
        description="Penguin Pad is the launchpad for bonding curve token launches and community presale raises on Robinhood Chain. Browse live projects and participate."
      />

      <section className="relative overflow-hidden">
        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 sm:pb-24 sm:pt-14 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <img
              src="https://static.lumi.new/d0/d0ece20a37e948cbfbb3684ecdb553f6.webp"
              alt="Penguin Pad logo - geometric penguin head in a lime ring above the PenguinPad.xyz wordmark"
              className="mx-auto h-52 w-auto drop-shadow-[0_0_28px_rgba(163,230,53,0.25)] sm:h-72 lg:h-80"
            />
            <span className="mt-7 inline-flex items-center gap-2 rounded-full border border-lime-400/25 bg-lime-400/10 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-lime-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-lime-400" />
              Robinhood Chain · permissionless · no KYC
            </span>
            <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-white sm:text-6xl lg:text-7xl">
              Launch on a curve.
              {' '}
              <span className="text-lime-400">Raise</span>
              {' '}
              with the Colony.
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
              Anyone with a wallet can launch a bonding-curve token or open a community presale on Robinhood Chain — no KYC, no waitlist.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/submit?type=launchpad"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-lime-400 px-6 py-3 text-sm font-semibold text-black shadow-[0_0_28px_rgba(163,230,53,0.32)] transition-all hover:bg-lime-300 sm:w-auto"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Create Quick Launch
              </Link>
              <Link
                to="/submit?type=presale"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-orange-400/40 bg-orange-400 px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-orange-300 sm:w-auto"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Create Presale
              </Link>
              <Link
                to="/launchpad"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-white/20 hover:bg-white/[0.06] sm:w-auto"
              >
                <Rocket className="h-4 w-4" aria-hidden="true" />
                Explore Launchpad
              </Link>
              <Link
                to="/presale"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/10 px-6 py-3 text-sm font-semibold text-zinc-300 transition-colors hover:border-white/20 hover:text-white sm:w-auto"
              >
                <TrendingUp className="h-4 w-4" aria-hidden="true" />
                Browse Presales
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-2">
          <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-lime-400/40">
            <div className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-lime-400/10 blur-2xl" aria-hidden="true" />
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-lime-400/20 bg-lime-400/10 text-lime-400">
              <Rocket className="h-5 w-5" aria-hidden="true" />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-white">Launchpad</h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-zinc-400">
              Fair-launch tokens on a bonding curve. Launch fee 0.00025 ETH.
              Bonds at 4.2 ETH into LP. Curve creator fees are disbursed at bonding; after that, a 1% Uniswap fee (75/25 creator/platform) is disbursed on every swap.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/launchpad"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-lime-400 hover:text-lime-300"
              >
                View live launches
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/submit?type=launchpad"
                className="inline-flex items-center gap-1.5 rounded-full border border-lime-400/30 bg-lime-400/10 px-3 py-1.5 text-xs font-semibold text-lime-300 transition-colors hover:bg-lime-400/20"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Create Quick Launch
              </Link>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-orange-400/40">
            <div className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-orange-400/10 blur-2xl" aria-hidden="true" />
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-orange-400/20 bg-orange-400/10 text-orange-400">
              <TrendingUp className="h-5 w-5" aria-hidden="true" />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-white">Presale</h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-zinc-400">
              Opening a raise costs 0.1 ETH. Creators set ETH soft/hard caps and per-wallet min/max. Penguin Pad takes 3% of ETH raised. Remaining ETH splits 70–100% LP / 0–30% creator. After listing, every Uniswap trade pays a 1% fee, 75/25 creator/platform, disbursed in that swap. Contribute during the start/end window; withdraw stays open until close or after cancel.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/presale"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-400 hover:text-orange-300"
              >
                View active raises
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/submit?type=presale"
                className="inline-flex items-center gap-1.5 rounded-full border border-orange-400/30 bg-orange-400/10 px-3 py-1.5 text-xs font-semibold text-orange-300 transition-colors hover:bg-orange-400/20"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Create Presale
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-10 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${
              listingTab === 'launches' ? 'text-lime-400' : 'text-orange-400'
            }`}
            >
              {listingTab === 'launches' ? 'Live curves' : 'Community raises'}
            </p>
            <h2 className="mt-1 text-2xl font-bold text-white">
              {listingTab === 'launches' ? 'Live Quick Launches' : 'Active Presale Raises'}
            </h2>
          </div>
          <Link
            to={listingTab === 'launches' ? '/launchpad' : '/presale'}
            className={`text-sm font-medium ${
              listingTab === 'launches' ? 'text-lime-400 hover:text-lime-300' : 'text-orange-400 hover:text-orange-300'
            }`}
          >
            See all
          </Link>
        </div>

        <div
          className="mt-5 grid grid-cols-2 gap-1 rounded-2xl border border-white/10 bg-white/[0.03] p-1"
          role="tablist"
          aria-label="Homepage listings"
        >
          <button
            type="button"
            role="tab"
            aria-selected={listingTab === 'launches'}
            onClick={() => setListingTab('launches')}
            className={`rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
              listingTab === 'launches'
                ? 'bg-lime-400 text-black'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            Quick Launches
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={listingTab === 'presales'}
            onClick={() => setListingTab('presales')}
            className={`rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
              listingTab === 'presales'
                ? 'bg-orange-400 text-black'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            Presales
          </button>
        </div>

        {listingTab === 'launches'
          ? (
              featuredCurves.length > 0
                ? (
                    <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-5">
                      {featuredCurves.map(launch => (
                        <SimLaunchCard key={launch.id} launch={launch} />
                      ))}
                    </div>
                  )
                : (
                    <p className="mt-5 rounded-2xl border border-dashed border-white/10 px-4 py-8 text-center text-sm text-zinc-500">
                      No live quick launches yet. Create one from Create Quick Launch.
                    </p>
                  )
            )
          : (
              featuredPresale.length > 0
                ? (
                    <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-5">
                      {featuredPresale.map(project => (
                        <ProjectCard key={project.id} project={project} />
                      ))}
                    </div>
                  )
                : (
                    <p className="mt-5 rounded-2xl border border-dashed border-white/10 px-4 py-8 text-center text-sm text-zinc-500">
                      No live presales yet. Open one from Create Presale.
                    </p>
                  )
            )}
      </section>

      <RecentlyBought />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
            <ShieldCheck className="h-5 w-5 text-lime-400" aria-hidden="true" />
            <p className="mt-3 text-sm font-semibold text-white">Transparent by design</p>
            <p className="mt-1 text-xs leading-relaxed text-zinc-500">On-chain progress on Robinhood Chain. Listings are not endorsements.</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
            <Rocket className="h-5 w-5 text-lime-400" aria-hidden="true" />
            <p className="mt-3 text-sm font-semibold text-white">Fair-launch curves</p>
            <p className="mt-1 text-xs leading-relaxed text-zinc-500">On-chain curve, 4.2 ETH bond. Curve fees (75% creator) are disbursed at bonding; Uniswap then pays 1% on every swap. Anyone can launch from a Robinhood Chain wallet.</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
            <TrendingUp className="h-5 w-5 text-orange-400" aria-hidden="true" />
            <p className="mt-3 text-sm font-semibold text-white">Community-funded raises</p>
            <p className="mt-1 text-xs leading-relaxed text-zinc-500">0.1 ETH to open. ETH caps and wallet min/max. 3% of ETH raised. LP 70–100%, creator 0–30%. After listing, 1% Uniswap fee with 75/25 creator/platform — disbursed on every swap.</p>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default Home
